/**
 * TransferHistory.tsx
 * Manager page for viewing transfer verification history (read-only).
 * Shows the history of verified transfers without verification functionality.
 */
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  inventoryService,
  VerifiedTransfer,
} from '../../services/inventoryService';
import toast from 'react-hot-toast';
import {
  CheckCircle,
  AlertTriangle,
  X,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  History,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

// Format timestamp for display
const formatVerifiedTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  // Subtract 5 hours to convert from server time to local CST
  date.setHours(date.getHours() - 5);
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const TransferHistory: React.FC = () => {
  const { t } = useLanguage();

  // History state
  const [historyTransfers, setHistoryTransfers] = useState<VerifiedTransfer[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [expandedHistoryTransfer, setExpandedHistoryTransfer] = useState<string | null>(null);

  // Load history on mount and page change
  useEffect(() => {
    loadHistory();
  }, [historyPage]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await inventoryService.getVerificationHistory({ page: historyPage, limit: 20 });
      setHistoryTransfers(data.transfers);
      setHistoryTotalPages(data.pagination.total_pages);
    } catch (err) {
      toast.error(t('transferVerification.historyLoadError'));
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <History className="w-6 h-6" />
              {t('nav.transferHistory')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('transferHistory.subtitle')}
            </p>
          </div>
          <button
            onClick={loadHistory}
            disabled={loadingHistory}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`w-5 h-5 ${loadingHistory ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Content */}
      {loadingHistory ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : historyTransfers.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">{t('transferVerification.noHistory')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {historyTransfers.map((transfer) => (
            <div
              key={transfer.transfer_number}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm"
            >
              {/* Transfer header - clickable */}
              <button
                onClick={() => setExpandedHistoryTransfer(
                  expandedHistoryTransfer === transfer.transfer_number ? null : transfer.transfer_number
                )}
                className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1 mr-2">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {t('transferVerification.transfer')} #{transfer.transfer_number}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatVerifiedTime(transfer.verified_at)}
                    </p>
                    {transfer.verified_by_name && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {transfer.verified_by_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Status badges */}
                    <div className="flex flex-wrap gap-1">
                      {transfer.approved_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full whitespace-nowrap">
                          {transfer.approved_count} {t('transferVerification.approved')}
                        </span>
                      )}
                      {transfer.on_hold_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full whitespace-nowrap">
                          {transfer.on_hold_count} {t('transferVerification.onHold')}
                        </span>
                      )}
                      {transfer.rejected_count > 0 && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-full whitespace-nowrap">
                          {transfer.rejected_count} {t('transferVerification.rejected')}
                        </span>
                      )}
                    </div>
                    {expandedHistoryTransfer === transfer.transfer_number ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded item details */}
              {expandedHistoryTransfer === transfer.transfer_number && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                  <div className="space-y-3">
                    {transfer.items.map((item, idx) => {
                      // Determine status
                      const isAccepted = item.verification_status === 'approved' || item.verification_status === 'verified';
                      const isOnHold = item.verification_status === 'on_hold';
                      const isRejected = item.verification_status === 'rejected';

                      const receivedQty = Math.round(parseFloat(String(item.actual_quantity || 0)));
                      const receivedWeight = parseFloat(String(item.actual_weight || 0));
                      const expectedQty = Math.round(parseFloat(String(item.expected_quantity || 0)));
                      const expectedWeight = parseFloat(String(item.expected_weight || 0));

                      return (
                        <div
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            isRejected
                              ? 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20'
                              : isOnHold
                              ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20'
                              : 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {item.item_name || item.item_number}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {item.item_number}
                              </p>
                            </div>
                            {isRejected ? (
                              <X className="w-6 h-6 text-red-600 dark:text-red-400" />
                            ) : isOnHold ? (
                              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                            ) : (
                              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                            )}
                          </div>

                          {/* Grid of expected/received/rejected */}
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                              <span className="text-gray-500 dark:text-gray-400 text-xs block">{t('transferVerification.expected')}:</span>
                              <p className="font-mono text-gray-900 dark:text-white text-base">
                                {expectedQty} / {expectedWeight.toFixed(2)} lbs
                              </p>
                            </div>
                            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                              <span className="text-green-600 dark:text-green-400 text-xs block">{t('transferVerification.received')}:</span>
                              <p className="font-mono text-green-600 dark:text-green-400 text-base">
                                {receivedQty} / {receivedWeight.toFixed(2)} lbs
                              </p>
                            </div>
                            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                              <span className={`text-xs block ${isOnHold ? 'text-amber-600 dark:text-amber-400' : isRejected ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{t('transferVerification.status')}:</span>
                              <p className={`font-mono text-base ${isOnHold ? 'text-amber-600 dark:text-amber-400' : isRejected ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                {isAccepted ? t('transferVerification.approved') : isOnHold ? t('transferVerification.onHold') : t('transferVerification.rejected')}
                              </p>
                            </div>
                          </div>

                          {/* Total received summary */}
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('transferVerification.received')}: <span className="font-medium">{receivedQty} {t('transferVerification.boxes')} / {receivedWeight.toFixed(2)} lbs</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          {historyTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                disabled={historyPage === 1}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
                {historyPage} / {historyTotalPages}
              </span>
              <button
                onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                disabled={historyPage === historyTotalPages}
                className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransferHistory;
